import React, { useState } from 'react';
import {
    Box,
    Typography,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    IconButton
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DynamicBreadcrumbs from '../Shared/Breadcrumb/index.tsx';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate } from 'react-router';
import FilePreviewModal from '../Entity/EntityData/DocumentModel.tsx';

interface Document {
    name: string;
    uploadedBy: string;
    date: string;
    type: string;
    status: 'Verified' | 'Pending Review' | 'Requires Update';
}

const documents: Document[] = [
    {
        name: 'Government ID',
        uploadedBy: 'John Smith',
        date: '2025-05-10',
        type: 'PDF',
        status: 'Verified',
    },
    {
        name: 'Employment Contract',
        uploadedBy: 'John Smith',
        date: '2025-05-09',
        type: 'PDF',
        status: 'Pending Review',
    },
    {
        name: 'NDA Agreement',
        uploadedBy: 'John Smith',
        date: '2025-05-08',
        type: 'PDF',
        status: 'Verified',
    },
    {
        name: 'Proof of Address',
        uploadedBy: 'Jane Doe',
        date: '2025-05-07',
        type: 'PDF',
        status: 'Requires Update',
    },
];

const getStatusChip = (status: string) => {
    switch (status) {
        case 'Verified':
            return <Chip label="Verified" color="primary" size="small" />;
        case 'Pending Review':
            return <Chip label="Pending Review" color="default" size="small" />;
        case 'Requires Update':
            return <Chip label="Requires Update" color="error" size="small" />;
        default:
            return null;
    }
};

const ViewDocument: React.FC = () => {
    const navigate = useNavigate();
    const [filePreview, setFilePreview] = useState({
        open: false,
        fileUrl: '',
        fileType: '',
    });

    const breadcrumbsData = [
        {
            label: <IconButton color="primary"><HomeIcon /></IconButton>,
            onClick: () => navigate('/')
        },
        { label: 'View Document' }
    ]
    return (
        <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <DynamicBreadcrumbs
                items={breadcrumbsData}
                onBreadcrumbClick={(e, item) => item?.onClick?.()}
            />
            <Typography variant="h4" gutterBottom>
                Team Document Access
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
                View and manage team member documents that have been uploaded for onboarding.
            </Typography>

            <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Onboarding Progress</Typography>
                <LinearProgress variant="determinate" value={100} sx={{ height: 8, borderRadius: 2, mt: 1, mb: 3 }} />
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>Document</strong></TableCell>
                            <TableCell><strong>Uploaded By</strong></TableCell>
                            <TableCell><strong>Date</strong></TableCell>
                            <TableCell><strong>Type</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Action</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {documents.map((doc, idx) => (
                            <TableRow key={idx}>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <InsertDriveFileIcon />
                                        {doc.name}
                                    </Box>
                                </TableCell>
                                <TableCell>{doc.uploadedBy}</TableCell>
                                <TableCell>{doc.date}</TableCell>
                                <TableCell>{doc.type}</TableCell>
                                <TableCell>{getStatusChip(doc.status)}</TableCell>
                                <TableCell>
                                    <Button variant="outlined" size="small" startIcon={<VisibilityIcon />} onClick={() =>
                                        setFilePreview({
                                            open: true,
                                            fileUrl: `/path/to/${doc.name.replace(/\s+/g, '_')}.pdf`, // or actual URL
                                            fileType: doc.type,
                                        })
                                    }>
                                        View
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <FilePreviewModal
                open={filePreview.open}
                handleClose={() => setFilePreview({ open: false, fileUrl: '', fileType: '' })}
                fileUrl={filePreview.fileUrl}
                fileType={filePreview.fileType}
            />
        </Box>
    );
};

export default ViewDocument;
